/**
 * Snapshot of the most recent failed payment for a subscription block.
 *
 * - `code`: error type with the shape `payment.<suffix>` (e.g. `payment.failed`).
 *   The presentation layer maps it to a badge text + description.
 * - `concept`: backend-provided description, used as the description fallback
 *   when `code` is not mapped on the front.
 * - `date`: ISO date of the failure. Reserved — not rendered today.
 */
export type LastPaymentError = Readonly<{ code: string; concept: string; date: string }>;

/**
 * Creates a validated LastPaymentError value object.
 *
 * @param raw - Raw last-payment-error data from infrastructure
 * @returns Frozen LastPaymentError
 * @throws Error if `code` is missing
 */
export const createLastPaymentError = (raw: { code: string; concept: string; date: string }): LastPaymentError => {
  if (!raw.code?.trim()) {
    throw new Error('Last payment error code is required');
  }

  return Object.freeze({ code: raw.code, concept: raw.concept, date: raw.date });
};
